/**
 * Phase 408 wave 3 task 3.2 (ADR-089) — the console chat panel's WIRE CONTRACT:
 * every URL the panel calls, and every closed vocabulary it branches on.
 *
 * <b>Dependency-free on purpose.</b> The console's test policy is node-only (no jsdom,
 * no @testing-library — reporting-tabs-single-source.test.ts:15-18), and
 * tsconfig.integration.json compiles `test/**` with `types: ["node"]` alone. Anything
 * that reaches `fetcher.ts` (import.meta.env, MSAL) or React Query cannot be imported
 * from an integration test, which is why the house pattern so far has been to MIRROR a
 * production helper into the test file (weekly-activity.test.ts:10-15) and grep the
 * source to keep the copy honest.
 *
 * A mirrored URL is exactly the "fixtures lie" trap: the test would keep passing against
 * a path the SPA no longer calls. So the paths live HERE, in a module with zero imports —
 * `chatQueries.ts`, `usePostChatMessage.ts` and `test/integration/console-chat.test.ts`
 * all import the same builders, and the spawned engine answers the literal URLs the panel
 * ships. A path typo is then a red integration test, not a silently dead panel.
 *
 * Server side of each contract: platform/workflow-engine/Http/ConsoleChatEndpoints.cs
 * (routes + query params) and Engine/Dispatchers/GatedCardStateStore.cs (card states).
 */

// ── Routes (relative to the engine origin the fetcher prepends) ────────────────

/** POST — send one message. Body: ConsoleChatMessageRequest. */
export const CHAT_MESSAGES_PATH = "/internal/v0/chat/messages";

/** GET — the calling principal's conversations in one tenant. */
export const CHAT_CONVERSATIONS_PATH = "/internal/v0/chat/conversations";

/**
 * Page size asked for on every read. Equals the endpoint's own ceiling
 * (`ConsoleChatEndpoints.MaxPageLimit` = 200): asking for MORE is refused with
 * `invalid_limit` rather than clamped, so this value and the server's ceiling are one
 * number in two places and must not drift apart.
 */
export const CHAT_PAGE_LIMIT = 200;

/** `GET /internal/v0/chat/conversations?tenant=…` — the panel's conversation list. */
export function chatConversationsPath(tenantId: string): string {
  const qs = new URLSearchParams({ tenant: tenantId, limit: String(CHAT_PAGE_LIMIT) });
  return `${CHAT_CONVERSATIONS_PATH}?${qs.toString()}`;
}

/**
 * `GET /internal/v0/chat/conversations/{id}/messages?tenant=…[&after=…]` — one thread.
 *
 * `after` is STRICTLY exclusive (omit or 0 reads from the start). The conversation id is
 * a client-generated opaque string, not a Guid, so it is percent-encoded into the path.
 */
export function chatMessagesPath(
  tenantId: string,
  conversationId: string,
  afterSeq = 0,
): string {
  const qs = new URLSearchParams({ tenant: tenantId, limit: String(CHAT_PAGE_LIMIT) });
  if (afterSeq > 0) qs.set("after", String(afterSeq));
  return `${CHAT_CONVERSATIONS_PATH}/${encodeURIComponent(conversationId)}/messages?${qs.toString()}`;
}

/** `POST /internal/v0/chat/conversations/{id}/decisions` — answer one gated card. */
export function chatDecisionsPath(conversationId: string): string {
  return `${CHAT_CONVERSATIONS_PATH}/${encodeURIComponent(conversationId)}/decisions`;
}

// ── Closed vocabularies the panel branches on ─────────────────────────────────

/**
 * `ConsoleChatMessageDto.direction` — how a renderer tells the operator's own message
 * from the platform's reply. NEVER by parsing `actor`, which answers a different
 * question (the audit identity).
 */
export const DIRECTION_INBOUND = "inbound";
export const DIRECTION_OUTBOUND = "outbound";

/**
 * `card.card_kind` — the persisted gated-card vocabulary
 * (SendChatMessageTool.BuildConsoleCardPayload + ConsoleInboundDispatcher.DecisionCardKind).
 */
export const CARD_QA_ANSWER = "qa_answer";
export const CARD_CLARIFICATION_SELECTION = "clarification_selection";
export const CARD_WRITE_CONFIRM = "write_confirm";
/** The receipt row the decisions POST persists — informational, never an affordance. */
export const CARD_DECISION = "decision";

/**
 * `ConsoleChatMessageDto.confirm_state` — GatedCardStateValues.
 *
 * ONE rule for this renderer, stated by the server's own vocabulary: a card is
 * actionable IF AND ONLY IF its state is `pending`. Every other member means the token
 * is spent, dead or unknowable, and a button offered against it can only be refused.
 */
export const CONFIRM_STATE_PENDING = "pending";
export const CONFIRM_STATE_EXECUTED = "executed";
export const CONFIRM_STATE_DECLINED = "declined";
export const CONFIRM_STATE_RESOLVED = "resolved";
export const CONFIRM_STATE_EXPIRED = "expired";
export const CONFIRM_STATE_UNKNOWN = "unknown";

/** Human copy for a card whose gate is no longer live. Keyed by confirm_state. */
export const CONFIRM_STATE_LABELS: Record<string, string> = {
  [CONFIRM_STATE_EXECUTED]: "Confirmed — this write has run.",
  [CONFIRM_STATE_DECLINED]: "Cancelled — nothing was written.",
  [CONFIRM_STATE_RESOLVED]: "Already answered.",
  [CONFIRM_STATE_EXPIRED]: "This card expired before it was answered — ask again for a fresh one.",
  [CONFIRM_STATE_UNKNOWN]: "This card is no longer answerable — reload the conversation.",
};

/** True exactly when the card behind this state may still be tapped. */
export function isActionable(confirmState: string | null | undefined): boolean {
  return confirmState === CONFIRM_STATE_PENDING;
}
